import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { VolumesTab, SuiviSeancesTab, ReglesTab } from "./EmploiDuTempsExtras";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const EMPTY = { class_name: "", day: "Lundi", start_time: "08:00", end_time: "09:00", subject: "", teacher_name: "", teacher_id: null, room: "" };

const WeekGrid = ({ slots, canManage, onDelete }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {DAYS.map(day => {
      const daySlots = slots.filter(s => s.day === day);
      return (
        <div key={day} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`day-${day}`}>
          <div className="px-4 py-2 bg-[#1E3A8A] text-white font-heading font-bold text-sm">{day}</div>
          <div className="p-3 space-y-2 min-h-[60px]">
            {daySlots.map(s => (
              <div key={s.id} className="flex items-start justify-between gap-2 border border-slate-100 rounded-lg p-2 bg-slate-50" data-testid={`slot-${s.id}`}>
                <div>
                  <div className="text-xs font-mono font-bold text-[#166534]">{s.start_time} – {s.end_time}</div>
                  <div className="text-sm font-semibold text-slate-800">{s.subject}</div>
                  <div className="text-[11px] text-slate-500">{s.teacher_name}{s.room ? ` · Salle ${s.room}` : ""}</div>
                </div>
                {canManage && (
                  <button onClick={() => onDelete(s)} className="text-red-400 hover:text-red-600" data-testid={`delete-slot-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {daySlots.length === 0 && <div className="text-xs text-slate-300 text-center py-3">—</div>}
          </div>
        </div>
      );
    })}
  </div>
);

export default function EmploiDuTempsPage() {
  const { user } = useAuth();
  const isFamily = ["student", "parent"].includes(user?.role);
  const canManage = ["scolarite", "admin", "super_admin", "director"].includes(user?.role);
  const canRules = ["admin", "super_admin", "director"].includes(user?.role);
  const canSuivi = canManage || ["founder", "staff"].includes(user?.role);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [slots, setSlots] = useState([]);
  const [myClasses, setMyClasses] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadFamily = async () => {
    try {
      const { data } = await api.get("/timetable/my");
      setMyClasses(data.classes);
      setSlots(data.slots);
      if (data.classes.length > 0 && !selectedClass) setSelectedClass(data.classes[0]);
    } catch { toast.error("Erreur de chargement"); }
  };

  const loadStaff = async (cls) => {
    try {
      const { data } = await api.get("/timetable", { params: cls ? { class_name: cls } : {} });
      setSlots(data);
    } catch { toast.error("Erreur de chargement"); }
  };

  useEffect(() => {
    if (isFamily) { loadFamily(); }
    else {
      api.get("/classes").then(({ data }) => {
        setClasses(data);
        if (data.length > 0) { setSelectedClass(data[0].name); loadStaff(data[0].name); }
      }).catch(() => {});
      if (canManage) {
        api.get("/users", { params: { role: "teacher" } }).then(({ data }) => setTeachers(data)).catch(() => {});
      }
    }
  }, []);

  const addSlot = async () => {
    if (!form.subject.trim()) return toast.error("Matière obligatoire");
    setSaving(true);
    try {
      await api.post("/timetable", { ...form, class_name: selectedClass });
      toast.success("Créneau ajouté");
      setDlgOpen(false);
      setForm({ ...EMPTY, day: form.day });
      loadStaff(selectedClass);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const deleteSlot = async (s) => {
    if (!window.confirm(`Supprimer ${s.subject} (${s.day} ${s.start_time}) ?`)) return;
    try { await api.delete(`/timetable/${s.id}`); loadStaff(selectedClass); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const shownSlots = isFamily
    ? slots.filter(s => !selectedClass || s.class_name === selectedClass)
    : slots;

  return (
    <div className="space-y-6" data-testid="timetable-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><CalendarDays className="h-3 w-3" /><span>EMPLOI DU TEMPS</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">
            {isFamily ? "Emploi du temps" : "Gestion des emplois du temps"}
          </h1>
          {isFamily && myClasses.length > 0 && (
            <p className="text-sm text-slate-500 mt-1">Classe{myClasses.length > 1 ? "s" : ""} : {myClasses.join(", ")}</p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {(isFamily ? myClasses.length > 1 : true) && (
            <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); if (!isFamily) loadStaff(v); }}>
              <SelectTrigger className="w-48" data-testid="class-select"><SelectValue placeholder="Classe" /></SelectTrigger>
              <SelectContent>
                {(isFamily ? myClasses.map(c => ({ name: c })) : classes).map(c => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canManage && selectedClass && (
            <Button onClick={() => setDlgOpen(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-slot-btn">
              <Plus className="h-4 w-4" />Ajouter un créneau
            </Button>
          )}
        </div>
      </div>

      {isFamily && myClasses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">
          Aucune classe associée à votre compte.
        </div>
      ) : (canManage || canSuivi) ? (
        <Tabs defaultValue="grille">
          <TabsList data-testid="timetable-tabs">
            <TabsTrigger value="grille" data-testid="tab-grille">Grille</TabsTrigger>
            {canManage && <TabsTrigger value="volumes" data-testid="tab-volumes">Volumes horaires</TabsTrigger>}
            {canSuivi && <TabsTrigger value="suivi" data-testid="tab-suivi">Suivi des séances</TabsTrigger>}
            {canRules && <TabsTrigger value="regles" data-testid="tab-regles">Règles</TabsTrigger>}
          </TabsList>
          <TabsContent value="grille" className="mt-4">
            <WeekGrid slots={shownSlots} canManage={canManage} onDelete={deleteSlot} />
          </TabsContent>
          {canManage && (
            <TabsContent value="volumes" className="mt-4">
              <VolumesTab selectedClass={selectedClass} />
            </TabsContent>
          )}
          {canSuivi && (
            <TabsContent value="suivi" className="mt-4">
              <SuiviSeancesTab />
            </TabsContent>
          )}
          {canRules && (
            <TabsContent value="regles" className="mt-4">
              <ReglesTab />
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <WeekGrid slots={shownSlots} canManage={canManage} onDelete={deleteSlot} />
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="slot-dialog">
          <DialogHeader><DialogTitle>Nouveau créneau — {selectedClass}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Jour</Label>
              <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                <SelectTrigger data-testid="slot-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Matière *</Label>
              <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Mathématiques" data-testid="slot-subject" />
            </div>
            <div>
              <Label>Début</Label>
              <Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start" />
            </div>
            <div>
              <Label>Fin</Label>
              <Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="slot-end" />
            </div>
            <div>
              <Label>Enseignant</Label>
              {teachers.length > 0 ? (
                <Select value={form.teacher_id || "none"} onValueChange={(v) => {
                  const t = teachers.find(t => t.id === v);
                  setForm({ ...form, teacher_id: t ? t.id : null, teacher_name: t ? t.full_name : "" });
                }}>
                  <SelectTrigger data-testid="slot-teacher"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun —</SelectItem>
                    {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.teacher_name} onChange={e => setForm({ ...form, teacher_name: e.target.value })} data-testid="slot-teacher" />
              )}
            </div>
            <div>
              <Label>Salle</Label>
              <Input value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} data-testid="slot-room" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Annuler</Button>
            <Button onClick={addSlot} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="slot-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
